import { createContext, useContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from 'react';
import { useAuth } from './authContext'; // adjust path if needed
import { useUserAPI } from '../services/userService'; // adjust path if needed
import dayjs from 'dayjs';

type Usage = {
    notesCount: number;
    formsCount: number;
    chatCount: number;
    planName: string;
    renewalDate: string;
};

type ResourceUsageContextType = {
    usage: Usage;
    setUsage: Dispatch<SetStateAction<Usage>>;
};

const ResourceUsageContext = createContext<ResourceUsageContextType | undefined>(undefined);

function getNextRenewalDate(
	planStartDate: string,
	planPrice?: string,
	planEndDate?: string,
): string {
	console.log('Calculating next renewal date for plan start date:', planStartDate);

	if (!planStartDate) return '';

	// If special plan price, just return planEndDate (formatted)
	if (planPrice === '$899.99' && planEndDate) {
		return dayjs(planEndDate).format('MMM D, YYYY');
	}

	const start = dayjs(planStartDate);
	const now = dayjs();

	// Calculate the next renewal month/year
	let nextMonth = now.month() + 1;
	let nextYear = now.year();
	if (nextMonth > 11) {
		nextMonth = 0;
		nextYear += 1;
	}

	// Anniversary day
	const anniversaryDay = start.date();
	// Last day of next month
	const lastDayOfNextMonth = dayjs(new Date(nextYear, nextMonth + 1, 0)).date();

	// If anniversary day doesn't exist in next month, use last day
	const renewalDay = Math.min(anniversaryDay, lastDayOfNextMonth);

	// Next renewal date
	const nextRenewal = dayjs(new Date(nextYear, nextMonth, renewalDay));
	return nextRenewal.format('MMM D, YYYY');
}

export const ResourceUsageProvider = ({ children }: { children: ReactNode }) => {
    const [usage, setUsage] = useState<Usage>({
        notesCount: 0,
        formsCount: 0,
        chatCount: 0,
        planName: '',
        renewalDate: '',
    });

    const { userData, planData } = useAuth();

    useEffect(() => {
        if (userData) {
            setUsage({
				notesCount: userData.notesCount ?? 0,
				formsCount: userData.formsCount ?? 0,
				chatCount: userData.chatCount ?? 0,
				// Safely coerce planName to string; avoid calling toString on undefined
				planName: planData?.planName?.toString() ?? '',
				renewalDate: getNextRenewalDate(planData?.planStartDate || '', planData?.planPrice, planData?.planEndDate) || '',
			});
        }
    }, [userData, planData]);

    return (
        <ResourceUsageContext.Provider value={{ usage, setUsage }}>
            {children}
        </ResourceUsageContext.Provider>
    );
};

export const useResourceUsage = () => {
    const context = useContext(ResourceUsageContext);
    if (!context) {
        throw new Error('useResourceUsage must be used within a ResourceUsageProvider');
    }
    return context;
};
